import React, { useState } from 'react';
import { TooltipBox, TooltipBadge } from '../styled';
import SegHoverLayer from './SegHoverLayer';
import { SegmentationLabel } from 'models/segmentation';

interface Props {
  frameDimensions: { width: number; height: number };
  allSegmentations: SegmentationLabel[];
  visibleSegmentations: Set<string>;
  wrapperRef: React.RefObject<HTMLDivElement | null>;
  stroke?: boolean;
  setFrameDimensions?: (d: { width: number; height: number }) => void;
  children?: React.ReactNode;
  onHover?: (
    seg: { id: string; x: number; y: number; label: string; color?: string } | null
  ) => void;
  highlightedSegId?: string;
  highlightedStrokeColor?: string;
  enableHighlightedContour?: boolean;
}

const SegICIDOverlay: React.FC<Props> = ({
  frameDimensions,
  allSegmentations,
  visibleSegmentations,
  wrapperRef,
  stroke = false,
  children,
  onHover,
  highlightedSegId,
  highlightedStrokeColor,
  enableHighlightedContour = false,
}) => {
  // No raster mask usage — rely on vector `paths` and the SVG hover layer for rendering/hover.
  const [localHoveredSeg, setLocalHoveredSeg] = useState<{
    id: string;
    x: number;
    y: number;
    label: string;
    color?: string;
  } | null>(null);

  // Track invalid overlays (none when using paths) and notify parent when hover changes
  const [localInvalidOverlayIds] = React.useState<Set<string>>(new Set<string>());

  React.useEffect(() => {
    if (typeof onHover === 'function') {
      onHover(localHoveredSeg);
    }
  }, [localHoveredSeg, onHover]);

  // No mask images available — allow SegHoverLayer to render based on `paths`.
  const hasMaskIds = undefined;

  return (
    <>
      {children}
      {/* SVG hover layer will render path overlays; no raster images used. */}
      <SegHoverLayer
        allSegmentations={allSegmentations}
        visibleSegmentations={visibleSegmentations}
        invalidOverlayIds={localInvalidOverlayIds}
        frameDimensions={frameDimensions}
        wrapperRef={wrapperRef}
        setHoveredSeg={seg => setLocalHoveredSeg(seg)}
        stroke={stroke}
        hasMaskIds={hasMaskIds}
        highlightedSegId={highlightedSegId}
        highlightedStrokeColor={highlightedStrokeColor}
        enableHighlightedContour={enableHighlightedContour}
      />
      {localHoveredSeg && (
        <TooltipBox
          data-testid="seg-tooltip"
          style={{ top: localHoveredSeg.y + 12, left: localHoveredSeg.x + 12 }}
        >
          <TooltipBadge style={{ background: localHoveredSeg.color || '#1890ff' }} />
          {localHoveredSeg.label}
        </TooltipBox>
      )}
    </>
  );
};

export default SegICIDOverlay;
